import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import moment from 'moment';
import ExportButton from '../common/ExportButton';
import { exportToCSV, exportToExcel, exportToPDF } from '../../utils/exportUtils';

const ReporteCxC = ({ month, year }) => {
    const { userData } = useAuth();
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totals, setTotals] = useState({
        current: 0,
        d30: 0,
        d60: 0,
        d90: 0,
        d90plus: 0,
        total: 0
    });

    useEffect(() => {
        if (!userData?.condominioId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                // "Report Date" is the reference point (End of selected month)
                const reportDate = moment().year(year).month(month).endOf('month');

                // 1. Fetch Residents (for names and units)
                const usersSnap = await getDocs(query(collection(db, "usuarios"), where("condominioId", "==", userData.condominioId)));
                const residents = {};
                usersSnap.forEach(doc => {
                    const d = doc.data();
                    residents[doc.id] = {
                        nombre: d.nombre || 'Desconocido',
                        unidad: d.numeroCasa || 'N/A'
                    };
                });

                // 2. Fetch ALL Charges to reconstruct history
                const cargosQ = query(
                    collection(db, "cargos"),
                    where("condominioId", "==", userData.condominioId)
                );

                const cargosSnap = await getDocs(cargosQ);

                const agingMap = {};

                cargosSnap.forEach(doc => {
                    const cargo = doc.data();

                    // Filter 1: Ignore charges created AFTER the report date
                    const createdAt = cargo.fechaCreacion?.toDate ? moment(cargo.fechaCreacion.toDate()) : moment();
                    if (createdAt.isAfter(reportDate, 'day')) return;

                    // Filter 2: Ignore Cancelled charges
                    if (cargo.estatus === 'cancelado') return;

                    // Reconstruct Logic:
                    let isActive = false;
                    let saldoAtDate = 0;
                    const fullAmount = Number(cargo.monto);

                    if (cargo.estatus === 'pagado') {
                        const paidAt = cargo.fechaPago?.toDate ? moment(cargo.fechaPago.toDate()) : null;

                        if (!paidAt) {
                            isActive = false;
                        } else if (paidAt.isAfter(reportDate)) {
                            // Paid AFTER the report window => It was unpaid THEN.
                            saldoAtDate = fullAmount;
                            isActive = true;
                        }
                    } else {
                        // 'pendiente' or 'parcial'
                        const paidSoFar = cargo.montoPagado || 0;
                        saldoAtDate = fullAmount - paidSoFar;
                        isActive = true;
                    }

                    if (!isActive || saldoAtDate <= 0.01) return;

                    const residentId = cargo.residenteId;
                    if (!residents[residentId]) return; // Skip deleted users/residents

                    if (!agingMap[residentId]) {
                        agingMap[residentId] = {
                            residentName: residents[residentId].nombre,
                            unit: residents[residentId].unidad,
                            current: 0,
                            d30: 0,
                            d60: 0,
                            d90: 0,
                            d90plus: 0,
                            total: 0
                        };
                    }

                    // Aging Calculation: Month-based (Per User Request)
                    const dueDate = cargo.fechaVencimiento?.toDate ? moment(cargo.fechaVencimiento.toDate()) : moment();
                    const dueMonth = moment(dueDate).startOf('month');
                    const reportMonthStart = moment(reportDate).startOf('month');

                    const monthsOverdue = reportMonthStart.diff(dueMonth, 'months');

                    // Buckets based on months elapsed
                    if (monthsOverdue <= 0) {
                        // Due this month or future -> Current
                        agingMap[residentId].current += saldoAtDate;
                    } else if (monthsOverdue === 1) {
                        // Due last month -> 1-30 days
                        agingMap[residentId].d30 += saldoAtDate;
                    } else if (monthsOverdue === 2) {
                        // Due 2 months ago -> 31-60 days
                        agingMap[residentId].d60 += saldoAtDate;
                    } else if (monthsOverdue === 3) {
                        // Due 3 months ago -> 61-90 days
                        agingMap[residentId].d90 += saldoAtDate;
                    } else {
                        // 4+ months ago -> +90 days
                        agingMap[residentId].d90plus += saldoAtDate;
                    }

                    agingMap[residentId].total += saldoAtDate;
                });

                // Convert Map to Array
                const result = Object.values(agingMap).sort((a, b) => b.total - a.total);

                setReportData(result);

                // Calculate Totals
                const newTotals = result.reduce((acc, row) => ({
                    current: acc.current + row.current,
                    d30: acc.d30 + row.d30,
                    d60: acc.d60 + row.d60,
                    d90: acc.d90 + row.d90,
                    d90plus: acc.d90plus + row.d90plus,
                    total: acc.total + row.total
                }), { current: 0, d30: 0, d60: 0, d90: 0, d90plus: 0, total: 0 });

                setTotals(newTotals);

            } catch (error) {
                console.error("Error generating AR Report:", error);
            }
            setLoading(false);
        };

        fetchData();
    }, [userData, month, year]);

    const getExportData = () => {
        return reportData.map(row => ({
            'Unidad': row.unit,
            'Residente': row.residentName,
            'Al Corriente': row.current,
            '1-30 Días': row.d30,
            '31-60 Días': row.d60,
            '61-90 Días': row.d90,
            '+90 Días': row.d90plus,
            'Total': row.total
        }));
    };

    const handleExportCSV = () => {
        exportToCSV(getExportData(), `Reporte_CxC_${moment().month(month).format('MMMM')}_${year}`);
    };

    const handleExportExcel = () => {
        exportToExcel(getExportData(), `Reporte_CxC_${moment().month(month).format('MMMM')}_${year}`, 'CxC', userData?.condominioNombre);
    };

    const handleExportPDF = () => {
        const columns = ['Unidad', 'Residente', 'Al Corriente', '1-30 Días', '31-60 Días', '61-90 Días', '+90 Días', 'Total'];
        const data = reportData.map(row => [
            row.unit,
            row.residentName,
            `$${new Intl.NumberFormat('es-MX').format(row.current)}`,
            `$${new Intl.NumberFormat('es-MX').format(row.d30)}`,
            `$${new Intl.NumberFormat('es-MX').format(row.d60)}`,
            `$${new Intl.NumberFormat('es-MX').format(row.d90)}`,
            `$${new Intl.NumberFormat('es-MX').format(row.d90plus)}`,
            `$${new Intl.NumberFormat('es-MX').format(row.total)}`
        ]);

        // Add totals
        data.push([
            '',
            'TOTALES',
            `$${new Intl.NumberFormat('es-MX').format(totals.current)}`,
            `$${new Intl.NumberFormat('es-MX').format(totals.d30)}`,
            `$${new Intl.NumberFormat('es-MX').format(totals.d60)}`,
            `$${new Intl.NumberFormat('es-MX').format(totals.d90)}`,
            `$${new Intl.NumberFormat('es-MX').format(totals.d90plus)}`,
            `$${new Intl.NumberFormat('es-MX').format(totals.total)}`
        ]);

        exportToPDF(
            `Reporte de Cobranza (CxC) - ${moment().month(month).format('MMMM')} ${year}`,
            columns,
            data,
            `Reporte_CxC_${year}_${month + 1}`,
            userData?.condominioNombre
        );
    };

    if (loading) return <div className="loading-state">Calculando antigüedad de saldos al {moment().month(month).year(year).endOf('month').format('DD/MM/YYYY')}...</div>;

    return (
        <div className="report-container fade-in">
            <div className="report-header-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div className="kpi-card-mini" style={{ background: '#f8f9fa', padding: '10px 20px', borderRadius: '8px', borderLeft: '4px solid #dc3545' }}>
                    <span style={{ fontSize: '0.9em', color: '#666' }}>Total Vencido (+1 día)</span>
                    <h3 style={{ margin: 0, color: '#dc3545' }}>
                        ${new Intl.NumberFormat('es-MX').format(totals.total - totals.current)}
                    </h3>
                </div>

                <ExportButton
                    onExportCSV={handleExportCSV}
                    onExportExcel={handleExportExcel}
                    onExportPDF={handleExportPDF}
                />
            </div>

            <div className="table-responsive">
                <table className="styled-table">
                    <thead>
                        <tr>
                            <th>Unidad</th>
                            <th>Residente</th>
                            <th style={{ textAlign: 'right' }}>Al Corriente</th>
                            <th style={{ textAlign: 'right', color: '#f59e0b' }}>1-30 Días</th>
                            <th style={{ textAlign: 'right', color: '#f97316' }}>31-60 Días</th>
                            <th style={{ textAlign: 'right', color: '#ef4444' }}>61-90 Días</th>
                            <th style={{ textAlign: 'right', color: '#b91c1c' }}>+90 Días</th>
                            <th style={{ textAlign: 'right', fontWeight: 'bold' }}>Total Deuda</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.map((row, idx) => (
                            <tr key={idx}>
                                <td style={{ fontWeight: '500' }}>{row.unit}</td>
                                <td>{row.residentName}</td>
                                <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(row.current)}</td>
                                <td style={{ textAlign: 'right', backgroundColor: row.d30 > 0 ? '#fffbeb' : 'transparent' }}>
                                    ${new Intl.NumberFormat('es-MX').format(row.d30)}
                                </td>
                                <td style={{ textAlign: 'right', backgroundColor: row.d60 > 0 ? '#fff7ed' : 'transparent' }}>
                                    ${new Intl.NumberFormat('es-MX').format(row.d60)}
                                </td>
                                <td style={{ textAlign: 'right', backgroundColor: row.d90 > 0 ? '#fef2f2' : 'transparent' }}>
                                    ${new Intl.NumberFormat('es-MX').format(row.d90)}
                                </td>
                                <td style={{ textAlign: 'right', backgroundColor: row.d90plus > 0 ? '#fef2f2' : 'transparent', fontWeight: row.d90plus > 0 ? 'bold' : 'normal', color: row.d90plus > 0 ? '#b91c1c' : 'inherit' }}>
                                    ${new Intl.NumberFormat('es-MX').format(row.d90plus)}
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                    ${new Intl.NumberFormat('es-MX').format(row.total)}
                                </td>
                            </tr>
                        ))}
                        {/* Totals Row */}
                        <tr style={{ backgroundColor: '#f3f4f6', fontWeight: 'bold' }}>
                            <td colSpan={2} style={{ textAlign: 'right' }}>Totales:</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.current)}</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.d30)}</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.d60)}</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.d90)}</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.d90plus)}</td>
                            <td style={{ textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(totals.total)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: '20px', fontSize: '0.85em', color: '#666' }}>
                <p><strong>Nota:</strong> Los días de mora se calculan hasta finales del mes seleccionado ({moment().month(month).format('MMMM')}).</p>
            </div>
        </div>
    );
};

export default ReporteCxC;
